<template>
  <div class="animated fadeIn fluxo-wrap">
    <div class="card-header">
      <div class="row align-items-center justify-content-between">
        <div class="col-md-8">
          <div class="fx-chips">
            <button
              v-for="tipo in tipoTicket"
              :key="tipo.id"
              type="button"
              class="fx-chip"
              :class="{ pinned: pinnedType === tipo.id }"
              :style="{ '--c': corTipo(tipo.id) }"
              @mouseenter="hoverChip(tipo.id)"
              @mouseleave="leaveChip"
              @click="pin(tipo.id)"
            >
              <span class="fx-chip-dot"></span>
              {{ tipo.desc || tipo.chave }}
              <span class="fx-chip-count" title="etapas">{{ etapasDoTipo(tipo.id).length }}</span>
            </button>
          </div>
        </div>
        <div class="col-md-4 text-right">
          <button type="button" class="button button-primary button-sm" @click="toggleFullscreen">
            <fa-icon icon="expand" /> Tela cheia
          </button>
        </div>
      </div>
    </div>

    <p class="fx-hint">
      Passe o mouse sobre um <b>tipo</b> para destacar seu fluxo e numerar as etapas ·
      clique para <b>fixar</b> · passe sobre uma <b>etapa</b> para ver seus detalhes.
      <template v-if="podeVerGatilhos">
        O selo <fa-icon icon="bolt" class="text-purple" /> indica uma automação e o selo
        <fa-icon icon="sitemap" class="text-cond" /> uma saída condicional.
        Clique em um <b>gatilho</b> no painel ao lado para ver a regra de execução.
      </template>
      <template v-else>
        O selo <fa-icon icon="sitemap" class="text-cond" /> indica uma saída condicional.
      </template>
    </p>

    <div v-if="erroCond" class="fx-erro">
      <fa-icon icon="info-circle" />
      Não foi possível carregar os desvios condicionais. O diagrama abaixo mostra apenas o fluxo sequencial.
      <a href="#" @click.prevent="fetchCondicionais">Tentar novamente</a>
    </div>

    <div class="row">
      <div class="col-12 col-lg-9">
        <div class="card fx-card" ref="fsTarget">
          <div class="fx-scroll" ref="scroll">
            <div class="fx-diagram" ref="diagram" @mouseleave="scheduleHide">
              <div v-if="!colunas.length" class="fx-empty-all">
                <fa-icon icon="inbox" class="text-muted mb-2" style="font-size: 3rem;" />
                <p class="text-muted mb-0">Nenhum tipo de ticket com etapas configuradas.</p>
              </div>

              <div v-else class="fx-cols" ref="cols" :style="{ paddingBottom: padBottom + 'px', paddingTop: padTop + 'px' }">
                <div v-for="(col, ci) in colunas" :key="ci" class="fx-col">
                  <div
                    v-for="key in col"
                    :key="key"
                    :ref="'n_' + key"
                    class="fx-node"
                    :class="nodeCls(key)"
                    :style="nodeStyle(key)"
                    @mouseenter="showPop(key, $event)"
                  >
                    <template v-if="key === 'root'">
                      <div class="fx-root">
                        <fa-icon icon="ticket-alt" />
                        <span>Abertura de ticket</span>
                        <span v-if="rootTemGatilho" class="fx-zap fx-zap-corner"><fa-icon icon="bolt" /></span>
                      </div>
                    </template>
                    <template v-else>
                      <div class="fx-stripe"></div>
                      <div class="fx-node-body">
                        <div class="fx-node-head">
                          <span v-if="nodeOrd(key)" class="fx-ord">{{ nodeOrd(key) }}</span>
                          <span class="fx-node-title">{{ nodes[key].desc || nodes[key].chave }}</span>
                          <span v-if="nodeCondOut(key).length" class="fx-fork"
                            :title="tituloCondOut(key)"><fa-icon icon="sitemap" /></span>
                          <span v-if="nodeHasZap(key)" class="fx-zap"><fa-icon icon="bolt" /></span>
                        </div>
                        <code class="fx-node-key">{{ nodes[key].chave }}</code>
                        <div class="fx-tags">
                          <span v-if="flagsPorNode[key].requerimento.valor" class="badge badge-info"
                            :class="{ 'fx-flag-div': flagsPorNode[key].requerimento.divergente }"
                            :title="tituloFlag(key, 'requerimento', 'Formulário')">
                            Formulário<template v-if="flagsPorNode[key].requerimento.divergente">*</template>
                          </span>
                          <span v-if="flagsPorNode[key].aguarda.valor" class="badge badge-warning"
                            :class="{ 'fx-flag-div': flagsPorNode[key].aguarda.divergente }"
                            :title="tituloFlag(key, 'aguarda', 'Aguarda cliente')">
                            Aguarda cliente<template v-if="flagsPorNode[key].aguarda.divergente">*</template>
                          </span>
                          <span v-if="flagsPorNode[key].final.valor" class="badge badge-success"
                            :class="{ 'fx-flag-div': flagsPorNode[key].final.divergente }"
                            :title="tituloFlag(key, 'final', 'Etapa final')">
                            Final<template v-if="flagsPorNode[key].final.divergente">*</template>
                          </span>
                          <span v-if="nodes[key].emptyTipo" class="badge badge-light">Sem etapas</span>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <svg class="fx-svg" :width="svgW" :height="svgH" :viewBox="`0 0 ${svgW} ${svgH}`">
                <g v-for="(e, i) in edges" :key="i">
                  <path class="fx-ln" :class="edgeCls(e)" :style="edgeStyle(e)" :d="e.d"></path>
                  <path class="fx-ah" :class="edgeCls(e)" :style="edgeFill(e)" :d="e.ah"></path>
                </g>
              </svg>

              <!-- popover de detalhes da etapa -->
              <div v-if="hovered" ref="pop" class="fx-pop" :style="popStyle"
                @mouseenter="cancelHide" @mouseleave="scheduleHide">
                <div class="fx-pop-title">{{ popNode.desc || popNode.chave || 'Abertura de ticket' }}</div>
                <code v-if="hovered !== 'root' && !popNode.emptyTipo" class="fx-pop-key">{{ popNode.chave }}</code>

                <template v-if="hovered === 'root'">
                  <div class="fx-pop-row"><span class="l">Papel</span><span class="v">Entrada de todos os tickets</span></div>
                  <div class="fx-pop-row"><span class="l">Ramifica em</span><span class="v">{{ tipoTicket.length }} tipos</span></div>
                </template>
                <template v-else-if="popNode.emptyTipo">
                  <div class="fx-pop-row"><span class="l">Situação</span><span class="v">Sem fluxo</span></div>
                  <div class="fx-pop-row"><span class="l">Tabela</span><span class="v">tipo_ticket_etapa vazio</span></div>
                </template>
                <template v-else>
                  <div class="fx-pop-row">
                    <span class="l">Requerimento</span>
                    <span class="v">{{ popFlags.requerimento.valor || '—' }}
                      <em v-if="popFlags.requerimento.divergente" class="fx-pop-div">só em {{ popFlags.requerimento.tipos.join(', ') }}</em>
                    </span>
                  </div>
                  <div class="fx-pop-row">
                    <span class="l">Aguarda cliente</span>
                    <span class="v">{{ popFlags.aguarda.valor ? 'Sim' : 'Não' }}
                      <em v-if="popFlags.aguarda.divergente" class="fx-pop-div">só em {{ popFlags.aguarda.tipos.join(', ') }}</em>
                    </span>
                  </div>
                  <div class="fx-pop-row">
                    <span class="l">Etapa final</span>
                    <span class="v">{{ popFlags.final.valor ? 'Sim' : 'Não' }}
                      <em v-if="popFlags.final.divergente" class="fx-pop-div">só em {{ popFlags.final.tipos.join(', ') }}</em>
                    </span>
                  </div>
                  <div class="fx-pop-row">
                    <span class="l">SLA máximo</span>
                    <span class="v">{{ popFlags.sla.valor || '—' }}
                      <em v-if="popFlags.sla.divergente" class="fx-pop-div">varia por tipo</em>
                    </span>
                  </div>
                  <div class="fx-pop-row">
                    <span class="l">Setor / fila</span>
                    <span class="v">{{ popFlags.setor.valor || '—' }}
                      <em v-if="popFlags.setor.divergente" class="fx-pop-div">varia por tipo</em>
                    </span>
                  </div>

                  <div class="fx-pop-sub">Ordem por tipo</div>
                  <div v-for="t in popTipoList" :key="'o' + t.tid" class="fx-pop-ord">
                    <span class="cd" :style="{ background: t.cor }"></span>
                    <span class="nm">{{ t.nome }}</span>
                    <span class="on" :style="{ color: t.cor }">etapa {{ t.ordem }}</span>
                  </div>

                  <template v-if="popCondOut.length || popCondIn.length">
                    <div class="fx-pop-sub">Etapas condicionais</div>
                    <div v-for="(c, i) in popCondOut" :key="'co' + i" class="fx-pop-cond">
                      <span class="cd" :style="{ background: c.cor }"></span>
                      <span class="nm">→ {{ c.destinoNome }}</span>
                      <span class="tp">{{ c.tipoNome }}</span>
                    </div>
                    <div v-for="(c, i) in popCondIn" :key="'ci' + i" class="fx-pop-cond in">
                      <span class="cd" :style="{ background: c.cor }"></span>
                      <span class="nm">← {{ c.origemNome }}</span>
                      <span class="tp">{{ c.tipoNome }}</span>
                    </div>
                  </template>

                  <div class="fx-pop-sub">Campos adicionais (escopo etapa)</div>
                  <div v-if="loadingCamposPop" class="fx-pop-muted">Carregando…</div>
                  <div v-else-if="erroCamposPop" class="fx-pop-erro">
                    Não foi possível carregar os campos desta etapa.
                  </div>
                  <template v-else>
                    <div v-for="(c, k) in popCampos" :key="'c' + k" class="fx-pop-row">
                      <span class="l">{{ c.label }}</span><span class="v">{{ c.formato }}</span>
                    </div>
                    <div v-if="!popCampos.length" class="fx-pop-muted">Nenhum vinculado a esta etapa</div>
                  </template>

                  <template v-if="podeVerGatilhos && popGatilhos.length">
                    <div class="fx-pop-sub">Automações nesta etapa</div>
                    <div v-for="g in popGatilhos" :key="'g' + g.id" class="fx-pop-auto">
                      <fa-icon icon="bolt" class="ai" />
                      <div>
                        <b>{{ g.nome }}</b>
                        <span v-if="!g.ativo"> (inativo)</span><br />
                        {{ eventoTexto(g.evento) }} → {{ acaoLabelGatilho(g) }}
                      </div>
                    </div>
                  </template>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-3">
        <div v-if="podeVerGatilhos" class="card">
          <div class="card-header d-flex align-items-center">
            <b>Gatilhos<template v-if="activeType"> · {{ nomeTipo(activeType) }}</template></b>
            <span class="badge badge-pill badge-secondary ml-auto">{{ railGatilhos.length }}</span>
          </div>
          <div class="card-body">
            <div v-if="!railGatilhos.length" class="text-muted small">
              {{ activeType ? 'Nenhum gatilho incide neste tipo.' : 'Nenhum gatilho configurado.' }}
            </div>
            <div v-for="gi in railGatilhos" :key="gi.g.id" class="fx-gatilho fx-gatilho-click"
              role="button" tabindex="0" title="Ver regra de execução"
              @click="abrirGatilho(gi)" @keyup.enter="abrirGatilho(gi)">
              <div class="d-flex justify-content-between align-items-center">
                <b>{{ gi.g.nome }}</b>
                <span class="badge" :class="gi.g.ativo ? 'badge-success' : 'badge-light'">{{ gi.g.ativo ? 'Ativo' : 'Inativo' }}</span>
              </div>
              <div class="fx-gatilho-onde">{{ gatilhoOnde(gi) }}</div>
              <div class="fx-gatilho-act">
                <span class="fx-act-ic" :style="{ background: acaoCor(gi.g) + '22', color: acaoCor(gi.g) }">
                  <fa-icon :icon="acaoIcone(gi.g)" />
                </span>
                {{ acaoLabelGatilho(gi.g) }}
                <span class="fx-gatilho-more">
                  {{ (gi.g.regras || []).length }} regra(s) <fa-icon icon="chevron-right" />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="condVisiveis.length || condOrfas.length || erroCond" class="card">
          <div class="card-header d-flex align-items-center">
            <b>Etapas condicionais<template v-if="activeType"> · {{ nomeTipo(activeType) }}</template></b>
            <span class="badge badge-pill badge-secondary ml-auto">{{ condVisiveis.length }}</span>
          </div>
          <div class="card-body">
            <div v-if="loadingCond" class="text-muted small">Carregando…</div>
            <div v-else-if="erroCond" class="text-danger small">
              Não foi possível carregar os desvios condicionais.
              <a href="#" @click.prevent="fetchCondicionais">Tentar novamente</a>
            </div>
            <div v-else-if="!condVisiveis.length" class="text-muted small">
              Nenhuma saída condicional neste tipo.
            </div>
            <div v-for="(c, i) in condVisiveis" :key="'cv' + i" class="fx-cond-item">
              <div class="fx-cond-flow">
                <span class="fx-cond-de">{{ c.origemNome }}</span>
                <fa-icon icon="chevron-right" class="fx-cond-arr" />
                <span class="fx-cond-para">{{ c.destinoNome }}</span>
              </div>
              <div class="small text-muted">
                <span class="cd" :style="{ background: c.cor }"></span> {{ c.tipoNome }}
              </div>
            </div>
            <div v-if="condOrfas.length" class="fx-cond-orfa">
              <b>{{ condOrfas.length }} condicional(is) sem etapa correspondente</b>
              <div v-for="(c, i) in condOrfas" :key="'cf' + i" class="small">
                {{ nomeTipo(+c.tipo_ticket_id) || ('tipo ' + c.tipo_ticket_id) }}:
                <code>{{ c.etapa_atual_chave }}</code> → <code>{{ c.proxima_etapa_chave }}</code>
              </div>
            </div>
          </div>
        </div>

        <div v-if="activeType" class="card">
          <div class="card-header">
            <b>Campos adicionais do tipo</b> · {{ nomeTipo(activeType) }}
          </div>
          <div class="card-body">
            <div v-if="loadingCamposTipo" class="text-muted small">Carregando…</div>
            <div v-else-if="erroCamposTipo" class="text-danger small">
              Não foi possível carregar os campos adicionais deste tipo.
              <a href="#" @click.prevent="recarregarCamposTipo">Tentar novamente</a>
            </div>
            <template v-else>
              <div v-for="(c, i) in camposTipoAtivo" :key="i" class="fx-campo">
                <div class="d-flex justify-content-between align-items-center">
                  <b>{{ c.label }}</b>
                  <span class="badge badge-light">tipo</span>
                </div>
                <div class="small text-muted">
                  {{ c.formato }}
                  <span v-if="c.obrigatorio"> · obrigatório</span>
                  <span v-if="c.visivel_formulario === false || c.visivel_formulario === 0"> · oculto no formulário</span>
                </div>
                <div v-if="sistemasArr(c.sistemas).length" class="small text-muted">
                  Sistemas: {{ sistemasArr(c.sistemas).join(', ') }}
                </div>
              </div>
              <div v-if="!camposTipoAtivo.length" class="text-muted small">
                Nenhum campo adicional vinculado a este tipo.
              </div>
            </template>
          </div>
        </div>

        <div class="card">
          <div class="card-header"><b>Legenda</b></div>
          <div class="card-body fx-legend">
            <div><span class="ldot" style="background:#3a3e81"></span> Etapa inicial</div>
            <div><span class="ldot" style="background:#4CAF50"></span> Etapa final</div>
            <div><span class="ldot" style="background:#FFC107"></span> Aguarda cliente</div>
            <div v-if="podeVerGatilhos"><fa-icon icon="bolt" class="text-purple" /> Automação incide na etapa</div>
            <div><fa-icon icon="sitemap" class="text-cond" /> Etapa com saída condicional</div>
            <div>
              <svg width="26" height="10" class="lln"><path d="M1 5 L25 5" /></svg>
              Transição condicional (tabela <code>ticket_etapa_condicional</code>)
            </div>
            <div class="text-muted small mt-2">
              Etapas com a mesma chave são exibidas uma única vez e compartilhadas entre os tipos.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- detalhe da automação (regra de execução) -->
    <modal name="fluxoGatilhoDetalhe" height="auto" width="820" :scrollable="true" :adaptive="true">
      <CloseModal :close="fecharGatilho" />
      <div v-if="modalGi && podeVerGatilhos" class="modalbox">
        <div class="modalbox-content fx-modal">
          <div class="fx-modal-head">
            <span class="fx-act-ic fx-act-ic-lg"
              :style="{ background: acaoCor(modalGi.g) + '22', color: acaoCor(modalGi.g) }">
              <fa-icon :icon="acaoIcone(modalGi.g)" />
            </span>
            <div class="fx-modal-head-txt">
              <h4>{{ modalGi.g.nome }}</h4>
              <div class="fx-modal-badges">
                <span class="badge" :class="modalGi.g.ativo ? 'badge-success' : 'badge-light'">
                  {{ modalGi.g.ativo ? 'Ativo' : 'Inativo' }}
                </span>
                <span class="badge badge-info">{{ eventoTexto(modalGi.g.evento) }}</span>
                <span class="badge badge-light">ordem {{ modalGi.g.ordem == null ? '—' : modalGi.g.ordem }}</span>
                <code>{{ modalGi.g.evento }}</code>
              </div>
            </div>
          </div>

          <div class="fx-modal-sec">
            <div class="fx-modal-sub"><fa-icon icon="play" /> Quando executa</div>
            <p class="fx-modal-frase">
              Dispara <b>{{ eventoTexto(modalGi.g.evento) }}</b>
              <template v-if="modalGi.g.regras && modalGi.g.regras.length">
                , desde que
                <b>{{ modalGi.g.validar_todas_regras ? 'todas as regras abaixo sejam verdadeiras (E)'
                  : 'pelo menos uma das regras abaixo seja verdadeira (OU)' }}</b>.
              </template>
              <template v-else>
                , <b>sem nenhuma regra de filtro</b> — vale para todos os tickets do evento.
              </template>
            </p>
            <div class="fx-modal-onde">
              <span class="l">Incide em</span>
              <span class="v">{{ gatilhoOnde(modalGi) }}</span>
            </div>
          </div>

          <div class="fx-modal-sec">
            <div class="fx-modal-sub"><fa-icon icon="filter" /> Regra de execução</div>
            <div v-if="!(modalGi.g.regras || []).length" class="text-muted small">
              Nenhuma regra cadastrada.
            </div>
            <table v-else class="table table-sm fx-modal-tab">
              <thead>
                <tr>
                  <th style="width: 34px">#</th>
                  <th>Coluna</th>
                  <th style="width: 130px">Condição</th>
                  <th>Valor</th>
                  <th style="width: 110px">Origem</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in modalGi.g.regras" :key="'r' + (r.id || i)">
                  <td>
                    <span class="fx-modal-op">{{ i === 0 ? '' : (modalGi.g.validar_todas_regras ? 'E' : 'OU') }}</span>
                  </td>
                  <td><code>{{ r.coluna }}</code></td>
                  <td>{{ condicaoLabel(r.condicao) }}</td>
                  <td>
                    <pre v-if="r.tipo === 'sql'" class="fx-modal-sql">{{ r.valor }}</pre>
                    <span v-else>{{ r.valor }}</span>
                  </td>
                  <td>
                    <span class="badge" :class="r.tipo === 'sql' ? 'badge-warning' : 'badge-light'">
                      {{ r.tipo === 'sql' ? 'SQL' : 'Padrão' }}
                    </span>
                    <span v-if="r.tipo === 'sql'" class="badge badge-light">{{ r.banco === 'erp' ? 'ERP' : 'Local' }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="fx-modal-sec">
            <div class="fx-modal-sub"><fa-icon icon="cogs" /> O que executa</div>
            <div v-if="!modalAcoes.length" class="text-muted small">Nenhuma ação cadastrada.</div>
            <div v-for="(a, i) in modalAcoes" :key="'a' + (a.id || i)" class="fx-modal-acao">
              <div class="fx-modal-acao-head">
                <span class="fx-modal-acao-n">{{ i + 1 }}</span>
                <b>{{ acaoTitulo(a) }}</b>
                <span class="badge badge-light ml-auto">{{ a.tipo_execucao || '—' }}</span>
              </div>
              <pre v-if="a.tipo_execucao && a.tipo_execucao !== 'JOB'" class="fx-modal-sql">{{ acaoCodigo(a) }}</pre>
              <code v-else class="fx-modal-classe">{{ acaoCodigo(a) }}</code>
              <div v-if="acaoParams(a).length" class="fx-modal-params">
                <div v-for="(p, k) in acaoParams(a)" :key="'p' + k" class="fx-modal-param">
                  <span class="l">{{ p.nome }}</span>
                  <span class="v">{{ p.valor === '' || p.valor === null ? '—' : p.valor }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="button" @click="fecharGatilho">Fechar</button>
      </div>
    </modal>
  </div>
</template>

<script>
import { get } from '@/helpers/apiRequest';
import CloseModal from '@/components/CloseModal';
import {
  CORES,
  KIND,
  montaGrafo,
  montaCondLista,
  flagsDoNode,
  etapasDoTipo,
  parseGatilho,
  classify,
  acoesDoGatilho,
  acaoCodigo,
  acaoTitulo,
  acaoParams,
  acaoLabelGatilho,
  condicaoLabel,
  eventoTexto,
} from './fluxo';

export default {
  name: 'FluxoTicket',
  components: { CloseModal },
  props: {
    tipoTicket:       { type: Array, default: () => [] },
    tipoTicketEtapa:  { type: Array, default: () => [] },
    setores:          { type: Array, default: () => [] },
    ticketAcaoObjeto: { type: Array, default: () => [] },
    // dicionário condicao -> descrição (/admin/sac/ticket-acao-regra/condicao)
    ticketAcaoRegraCondicao: { type: [Object, Array], default: () => ({}) },
    // catálogo das ações JOB, com label e nomes dos parâmetros
    ticketAcaoExecucaoAcao:  { type: Array, default: () => [] },
    // permissão da aba de gatilhos (SAC_TICKET_ACAO_OBJETO): sem ela a tela não
    // mostra a configuração das automações, que é o conteúdo daquela aba
    podeVerGatilhos: { type: Boolean, default: false },
    // a aba nasce montada e oculta; a carga paginada só roda quando ela é aberta
    ativa: { type: Boolean, default: false },
  },
  data() {
    return {
      nodes: {},
      colunas: [],
      baseEdges: [],
      edges: [],
      svgW: 0,
      svgH: 0,
      activeType: null,
      pinnedType: null,
      hovered: null,
      popStyle: {},
      camposCache: {},        // etapaId -> [campos vinculados]
      camposTipoCache: {},    // tipoId -> [campos vinculados]
      loadingCamposPop: false,
      loadingCamposTipo: false,
      ro: null,               // ResizeObserver
      redrawPending: false,
      padBottom: 0,           // espaço p/ o canal de roteamento das arestas longas
      padTop: 0,              // espaço p/ o canal das condicionais que retrocedem
      hideTimer: null,
      condicionais: [],       // ticket_etapa_condicional (todas as páginas)
      loadingCond: false,
      condCarregado: false,   // evita repetir a carga a cada ativação da aba
      erroCond: false,
      erroCamposPop: false,
      erroCamposTipo: false,
      condEdges: [],          // condicionais resolvidas no grafo
      condOrfas: [],          // condicionais cuja chave não existe no tipo
      modalGi: null,          // gatilho aberto no modal de detalhe
    };
  },
  computed: {
    rootTemGatilho() {
      return this.podeVerGatilhos && this.ticketAcaoObjeto.some((g) => g.evento === 'TicketCriado');
    },
    // atributos das etapas prontos p/ exibição: da linha do tipo em foco quando
    // há um; sem foco, com marca de divergência entre os tipos
    flagsPorNode() {
      const out = {};
      Object.keys(this.nodes).forEach((k) => {
        out[k] = flagsDoNode(this.nodes[k], this.activeType, this.nomeTipo);
      });
      return out;
    },
    popFlags() {
      return (this.hovered && this.flagsPorNode[this.hovered]) || flagsDoNode(null);
    },
    popNode() {
      if (!this.hovered) return {};
      return this.nodes[this.hovered] || {};
    },
    popTipoList() {
      const n = this.popNode;
      if (!n || n.root || n.emptyTipo != null || !n.tipos) return [];
      return Object.entries(n.tipos).map(([tid, info]) => ({
        tid: Number(tid), etapaId: info.etapaId, ordem: info.ordem,
        nome: this.nomeTipo(Number(tid)), cor: this.corTipo(Number(tid)),
      }));
    },
    popCampos() {
      const out = [];
      this.popTipoList.forEach((t) => {
        (this.camposCache[t.etapaId] || []).forEach((c) => out.push(c));
      });
      return out;
    },
    camposTipoAtivo() {
      return this.activeType ? (this.camposTipoCache[this.activeType] || []) : [];
    },
    // parseia cada gatilho -> a quais tipos/etapas ele se aplica (pela condição)
    gatilhosInfo() {
      if (!this.podeVerGatilhos) return [];
      return (this.ticketAcaoObjeto || []).map((g) => ({ g, ...this.parseGatilho(g) }));
    },
    // gatilhos exibidos no painel: filtrados pelo tipo em foco
    railGatilhos() {
      if (this.activeType == null) return this.gatilhosInfo;
      return this.gatilhosInfo.filter((gi) => gi.global || gi.tipoIds.includes(this.activeType));
    },
    // gatilhos que incidem na etapa em hover (popover)
    popGatilhos() {
      if (!this.hovered || !this.podeVerGatilhos) return [];
      return this.gatilhosDoNode(this.hovered);
    },
    // transições condicionais já resolvidas, com nomes prontos p/ exibição
    condLista() {
      return montaCondLista(this.condEdges, {
        nomeNode: this.nomeNode,
        nomeTipo: this.nomeTipo,
        corTipo:  this.corTipo,
      });
    },
    // condicionais do painel lateral: filtradas pelo tipo em foco
    condVisiveis() {
      if (this.activeType == null) return this.condLista;
      return this.condLista.filter((c) => c.tipoId === this.activeType);
    },
    // saídas condicionais da etapa em hover
    popCondOut() {
      if (!this.hovered) return [];
      return this.condVisiveis.filter((c) => c.origem === this.hovered);
    },
    // entradas condicionais na etapa em hover
    popCondIn() {
      if (!this.hovered) return [];
      return this.condVisiveis.filter((c) => c.destino === this.hovered);
    },
    modalAcoes() {
      if (!this.modalGi) return [];
      return acoesDoGatilho(this.modalGi.g);
    },
  },
  watch: {
    tipoTicketEtapa() { this.rebuild(); },
    tipoTicket() { this.rebuild(); },
    ticketAcaoObjeto() { this.rebuild(); },
    ativa(valor) { if (valor) this.garanteCondicionais(); },
  },
  mounted() {
    // o CTabs monta todas as abas; a carga paginada espera a aba ser aberta
    if (this.ativa) this.garanteCondicionais();
    this.rebuild();
    window.addEventListener('resize', this.onResize);
    document.addEventListener('fullscreenchange', this.onResize);
    // observa o container de scroll (estável) — a aba nasce oculta (display:none)
    // e o SVG é dimensionado pelas colunas, evitando loop do ResizeObserver
    if (typeof ResizeObserver !== 'undefined' && this.$refs.scroll) {
      this.ro = new ResizeObserver(() => this.onResize());
      this.ro.observe(this.$refs.scroll);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('fullscreenchange', this.onResize);
    if (this.ro) { this.ro.disconnect(); this.ro = null; }
    this.cancelHide();
  },
  methods: {
    corTipo(tipoId) {
      const idx = this.tipoTicket.findIndex((t) => t.id === tipoId);
      return CORES[(idx < 0 ? 0 : idx) % CORES.length];
    },
    nomeTipo(tipoId) {
      const t = this.tipoTicket.find((x) => x.id === tipoId);
      return t ? (t.desc || t.chave) : '';
    },
    etapasDoTipo(tipoId) {
      return etapasDoTipo(this.tipoTicketEtapa, tipoId);
    },
    // `sistemas` pode vir como array, string JSON ('["SAC"]') ou "A,B"
    sistemasArr(v) {
      if (Array.isArray(v)) return v;
      if (typeof v === 'string' && v.trim()) {
        try {
          const p = JSON.parse(v);
          return Array.isArray(p) ? p : [String(p)];
        } catch (e) {
          return v.split(',').map((s) => s.trim()).filter(Boolean);
        }
      }
      return [];
    },

    /* ---------- construção do grafo ---------- */
    rebuild() {
      if (!this.tipoTicket.length) { this.colunas = []; return; }
      const g = montaGrafo({
        tipoTicket:      this.tipoTicket,
        tipoTicketEtapa: this.tipoTicketEtapa,
        condicionais:    this.condicionais,
        nomeSetor:       this.nomeSetor,
      });
      this.nodes = g.nodes;
      this.colunas = g.colunas;
      this.condEdges = g.condEdges;
      this.condOrfas = g.condOrfas;
      this.padBottom = g.padBottom;
      this.padTop = g.padTop;
      this.baseEdges = g.baseEdges;
      this.edges = [];
      this.$nextTick(() => this.drawEdges());
    },
    nomeSetor(setorId) {
      if (!setorId) return null;
      const s = this.setores.find((x) => x.id === setorId);
      return s ? (s.desc || s.chave) : null;
    },

    /* ---------- etapas condicionais ---------- */
    nomeNode(key) {
      const n = this.nodes[key];
      return n ? (n.desc || n.chave || key) : key;
    },
    // saídas condicionais da etapa, respeitando o tipo em foco
    nodeCondOut(key) {
      return this.condVisiveis.filter((c) => c.origem === key);
    },
    tituloCondOut(key) {
      const saidas = this.nodeCondOut(key);
      if (!saidas.length) return '';
      return 'Saída condicional: ' + saidas.map((c) => `${c.destinoNome} (${c.tipoNome})`).join(' · ');
    },
    garanteCondicionais() {
      if (this.condCarregado || this.loadingCond) return null;
      return this.fetchCondicionais();
    },
    // o endpoint é paginado (10/pág.) e o grafo precisa de todas as transições
    fetchCondicionais() {
      if (this.loadingCond) return;
      this.loadingCond = true;
      this.erroCond = false;
      const acumulado = [];
      const puxaPagina = (page) => get(`/admin/sac/ticket-etapa-condicional?page=${page}`)
        .then((json) => {
          (json.itens || []).forEach((i) => acumulado.push(i));
          const ultima = (json.paginator && json.paginator.last_page) || 1;
          if (page < ultima) return puxaPagina(page + 1);
          return null;
        });

      return puxaPagina(1)
        // a paginação é sequencial: uma falha no meio entrega parte dos desvios,
        // e um diagrama incompleto é indistinguível de um fluxo sem desvio
        .catch(() => { this.erroCond = true; })
        .finally(() => {
          this.condicionais = acumulado;
          this.condCarregado = !this.erroCond;
          this.loadingCond = false;
          this.rebuild();
        });
    },

    /* ---------- conexões (SVG medido) ---------- */
    onResize() {
      if (this.redrawPending) return;
      this.redrawPending = true;
      requestAnimationFrame(() => { this.redrawPending = false; this.drawEdges(); });
    },
    anchor(key, side) {
      let r = this.$refs['n_' + key];
      if (Array.isArray(r)) r = r[0];
      if (!r) return null;
      const el = r.$el || r;
      const dr = this.$refs.diagram.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      return {
        x: (side === 'r' ? b.right : b.left) - dr.left,
        y: b.top - dr.top + b.height / 2,
      };
    },
    drawEdges() {
      const diag = this.$refs.diagram;
      const cols = this.$refs.cols;
      if (!diag || !cols || !this.baseEdges.length) { this.edges = []; return; }
      // aba ainda oculta (display:none) -> sem layout; aguarda o ResizeObserver
      if (!diag.offsetWidth) return;
      const dr = diag.getBoundingClientRect();

      // base do canal inferior = borda de baixo da caixa mais baixa
      // topo do canal superior = borda de cima da caixa mais alta
      let maxBottom = 0;
      let minTop = Infinity;
      Object.keys(this.nodes).forEach((k) => {
        let r = this.$refs['n_' + k];
        if (Array.isArray(r)) r = r[0];
        if (!r) return;
        const el = r.$el || r;
        const bb = el.getBoundingClientRect();
        const yb = bb.bottom - dr.top;
        const yt = bb.top - dr.top;
        if (yb > maxBottom) maxBottom = yb;
        if (yt < minTop) minTop = yt;
      });
      if (minTop === Infinity) minTop = 0;

      // lanes p/ arestas longas (uma "faixa" por aresta, empilhadas no canal)
      const laneOf = {};
      const laneTopOf = {};
      let lane = 0;
      let laneTop = 0;
      this.baseEdges.forEach((e) => {
        const span = this.nodes[e.b].layer - this.nodes[e.a].layer;
        if (span > 1) laneOf[this.edgeKey(e)] = lane++;
        else if (e.cond && span <= 0) laneTopOf[this.edgeKey(e)] = laneTop++;
      });

      const out = [];
      this.baseEdges.forEach((e) => {
        const a = this.anchor(e.a, 'r');
        const b = this.anchor(e.b, 'l');
        if (!a || !b) return;
        const span = this.nodes[e.b].layer - this.nodes[e.a].layer;
        let d;
        if (e.cond && span <= 0) {
          // condicional que retrocede (ou fica na mesma coluna): sobe pelo vão após a
          // origem, cruza pelo canal acima das caixas e desce no vão antes do destino
          const yc = minTop - 12 - (laneTopOf[this.edgeKey(e)] || 0) * 13;
          const x1 = a.x + 16;
          const x2 = b.x - 16;
          d = `M${a.x} ${a.y} L${x1} ${a.y} L${x1} ${yc} L${x2} ${yc} L${x2} ${b.y} L${b.x - 1} ${b.y}`;
        } else if (span > 1) {
          // desce pelo vão após a origem, cruza pelo canal (abaixo das caixas), sobe pelo vão antes do destino
          const yc = maxBottom + 12 + (laneOf[this.edgeKey(e)] || 0) * 13;
          const x1 = a.x + 16;
          const x2 = Math.max(x1 + 8, b.x - 16);
          d = `M${a.x} ${a.y} L${x1} ${a.y} L${x1} ${yc} L${x2} ${yc} L${x2} ${b.y} L${b.x - 1} ${b.y}`;
        } else if (Math.abs(a.y - b.y) < 0.5) {
          d = `M${a.x} ${a.y} L${b.x - 1} ${b.y}`;
        } else {
          // vão entre colunas adjacentes: sobe/desce no meio do vão (nunca sobre uma caixa)
          const mx = Math.max(a.x + 16, b.x - 26);
          d = `M${a.x} ${a.y} L${mx} ${a.y} L${mx} ${b.y} L${b.x - 1} ${b.y}`;
        }
        out.push({ ...e, d, ah: `M${b.x} ${b.y} l-7 -4 l0 8 z` });
      });

      // dimensiona o SVG pelo tamanho das COLUNAS (não do diagrama) — o svg é irmão
      // das colunas, então isso não realimenta o ResizeObserver
      this.svgW = cols.offsetWidth;
      this.svgH = Math.max(cols.offsetHeight, maxBottom + 12 + lane * 13 + 12);
      this.edges = out;
    },

    /* ---------- destaque por tipo ---------- */
    setActive(tipoId) {
      this.activeType = (tipoId === undefined ? null : tipoId);
      this.erroCamposTipo = false;
      if (this.activeType != null) this.fetchCamposTipo(this.activeType);
    },
    recarregarCamposTipo() {
      if (this.activeType != null) this.fetchCamposTipo(this.activeType);
    },
    hoverChip(tipoId) {
      // com um chip fixado, o hover não altera a visualização
      if (this.pinnedType == null) this.setActive(tipoId);
    },
    leaveChip() {
      if (this.pinnedType == null) this.setActive(null);
    },
    pin(tipoId) {
      this.pinnedType = (this.pinnedType === tipoId ? null : tipoId);
      this.setActive(this.pinnedType);
    },
    nodeOn(key) {
      if (this.activeType == null) return true;
      const n = this.nodes[key];
      if (!n) return false;
      if (n.root) return true;
      if (n.emptyTipo != null) return n.emptyTipo === this.activeType;
      return n.tipos[this.activeType] != null;
    },
    nodeOrd(key) {
      const n = this.nodes[key];
      if (this.activeType == null || !n || n.root || !n.tipos[this.activeType]) return '';
      return n.tipos[this.activeType].ordem;
    },
    nodeCls(key) {
      const n = this.nodes[key];
      if (!n) return {};
      const on = this.nodeOn(key);
      const f = this.flagsPorNode[key] || {};
      const final = !!(f.final && f.final.valor);
      const aguarda = !!(f.aguarda && f.aguarda.valor);
      return {
        'fx-root-node': n.root,
        'fx-start': !n.root && !n.emptyTipo && Object.values(n.tipos).some((t) => t.ordem === 1),
        'fx-final': final,
        'fx-wait': aguarda && !final,
        'fx-empty': n.emptyTipo != null,
        'fx-hl': this.activeType != null && on,
        'fx-dim': this.activeType != null && !on,
      };
    },
    nodeStyle(key) {
      return (this.activeType != null && this.nodeOn(key)) ? { '--hl': this.corTipo(this.activeType) } : {};
    },
    edgeCls(e) {
      return {
        'fx-cond': !!e.cond,
        'fx-dim': this.activeType != null && !e.types.includes(this.activeType),
      };
    },
    edgeKey(e) {
      return e.k || (e.a + '|' + e.b);
    },
    edgeStyle(e) {
      if (this.activeType != null && e.types.includes(this.activeType)) {
        return { stroke: this.corTipo(this.activeType), strokeWidth: '2.6' };
      }
      return {};
    },
    edgeFill(e) {
      if (this.activeType != null && e.types.includes(this.activeType)) {
        return { fill: this.corTipo(this.activeType) };
      }
      return {};
    },

    /* ---------- popover ---------- */
    showPop(key, ev) {
      // com um tipo em foco, etapas fora da trajetória (esmaecidas) não respondem ao hover
      if (this.activeType != null && !this.nodeOn(key)) { this.scheduleHide(); return; }
      this.cancelHide();
      this.hovered = key;
      this.erroCamposPop = false;
      const el = ev.currentTarget;
      const n = this.nodes[key];
      if (n && !n.root && n.emptyTipo == null && n.tipos) {
        this.fetchCamposEtapas(Object.values(n.tipos).map((t) => t.etapaId));
      }
      this.$nextTick(() => this.positionPop(el));
    },
    positionPop(el) {
      if (!el) return;
      this.$nextTick(() => {
        const r = el.getBoundingClientRect();
        let pop = this.$refs.pop;
        if (Array.isArray(pop)) pop = pop[0];
        const pw = pop ? pop.offsetWidth : 240;
        const ph = pop ? pop.offsetHeight : 0;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const pad = 8;
        let left = r.left;
        if (left + pw + pad > vw) left = Math.max(pad, vw - pw - pad);
        let top = r.bottom + pad;                    // abaixo do nó por padrão
        if (ph && top + ph + pad > vh) {             // não cabe embaixo -> tenta acima
          const above = r.top - ph - pad;
          top = above >= pad ? above : Math.max(pad, vh - ph - pad);
        }
        this.popStyle = { left: left + 'px', top: top + 'px', maxHeight: (vh - 2 * pad) + 'px' };
      });
    },
    scheduleHide() {
      this.cancelHide();
      this.hideTimer = setTimeout(() => { this.hovered = null; }, 150);
    },
    cancelHide() {
      if (this.hideTimer) { clearTimeout(this.hideTimer); this.hideTimer = null; }
    },

    /* ---------- campos adicionais (lazy) ---------- */
    // normaliza os campos: garante `sistemas` sempre como array
    normalizeCampos(json) {
      return (json || [])
        .filter((c) => c.vinculado)
        .map((c) => ({ ...c, sistemas: this.sistemasArr(c.sistemas) }));
    },
    fetchCamposEtapas(etapaIds) {
      const pend = etapaIds.filter((id) => id && this.camposCache[id] === undefined);
      if (!pend.length) return null;
      this.loadingCamposPop = true;
      this.erroCamposPop = false;
      return Promise.all(pend.map((id) =>
        get(`/admin/sac/tipo-ticket-etapa/${id}/campos-adicionais`)
          .then((json) => this.$set(this.camposCache, id, this.normalizeCampos(json)))
          // sem gravar cache: um `[]` permanente esconderia os campos p/ sempre
          .catch(() => { this.erroCamposPop = true; })
      )).finally(() => { this.loadingCamposPop = false; });
    },
    fetchCamposTipo(tipoId) {
      if (this.camposTipoCache[tipoId] !== undefined) return null;
      this.loadingCamposTipo = true;
      this.erroCamposTipo = false;
      return get(`/admin/sac/tipo-ticket/${tipoId}/campos-adicionais`)
        .then((json) => this.$set(this.camposTipoCache, tipoId, this.normalizeCampos(json)))
        // sem gravar cache: o próximo foco no tipo tenta de novo
        .catch(() => { this.erroCamposTipo = true; })
        .finally(() => { this.loadingCamposTipo = false; });
    },

    /* ---------- ações / gatilhos ---------- */
    parseGatilho(g) {
      return parseGatilho(g, { tipoTicket: this.tipoTicket, tipoTicketEtapa: this.tipoTicketEtapa });
    },
    nodeHasZap(key) {
      if (!this.podeVerGatilhos) return false;
      if (key === 'root') return this.gatilhosInfo.some((gi) => gi.g.evento === 'TicketCriado');
      return this.gatilhosInfo.some((gi) => gi.etapaChaves.includes(key));
    },
    gatilhosDoNode(key) {
      if (!this.podeVerGatilhos) return [];
      const list = key === 'root'
        ? this.gatilhosInfo.filter((gi) => gi.g.evento === 'TicketCriado')
        : this.gatilhosInfo.filter((gi) => gi.etapaChaves.includes(key));
      return list.map((gi) => gi.g);
    },
    gatilhoOnde(gi) {
      const ev = this.eventoTexto(gi.g.evento);
      if (gi.etapaChaves.length) {
        const nomes = gi.etapaChaves.map((ch) => {
          const e = this.tipoTicketEtapa.find((x) => x.chave === ch);
          return e ? (e.desc || ch) : ch;
        });
        return `${ev} · etapa: ${nomes.join(', ')}`;
      }
      if (gi.global) return `${ev} · todos os tipos`;
      const nomes = gi.tipoIds.map((id) => this.nomeTipo(id)).filter(Boolean);
      return `${ev} · tipo: ${nomes.join(', ')}`;
    },
    primeiraAcao(g) {
      return acoesDoGatilho(g)[0] || {};
    },
    acaoIcone(g) { return KIND[classify(this.primeiraAcao(g))].icon; },
    acaoCor(g) { return KIND[classify(this.primeiraAcao(g))].cor; },
    acaoLabelGatilho(g) { return acaoLabelGatilho(g); },
    eventoTexto(ev) { return eventoTexto(ev); },

    /* ---------- modal de detalhe da automação ---------- */
    abrirGatilho(gi) {
      // a configuração da automação é o conteúdo da aba de gatilhos
      if (!this.podeVerGatilhos) return;
      this.modalGi = gi;
      this.cancelHide();
      this.hovered = null;
      this.$modal.show('fluxoGatilhoDetalhe');
    },
    fecharGatilho() {
      this.$modal.hide('fluxoGatilhoDetalhe');
      this.modalGi = null;
    },
    condicaoLabel(cond) { return condicaoLabel(this.ticketAcaoRegraCondicao, cond); },
    acaoCodigo(a) { return acaoCodigo(a); },
    acaoTitulo(a) { return acaoTitulo(a, this.ticketAcaoExecucaoAcao); },
    acaoParams(a) { return acaoParams(a, this.ticketAcaoExecucaoAcao); },
    // rótulo da divergência de atributo entre os tipos que compartilham a etapa
    tituloFlag(key, campo, rotulo) {
      const f = (this.flagsPorNode[key] || {})[campo];
      if (!f || !f.divergente) return '';
      return `${rotulo}: vale apenas em ${f.tipos.join(', ')}`;
    },

    /* ---------- fullscreen ---------- */
    toggleFullscreen() {
      const el = this.$refs.fsTarget;
      if (!document.fullscreenElement) { el.requestFullscreen && el.requestFullscreen(); }
      else { document.exitFullscreen(); }
    },
  },
};
</script>

<style scoped>
.fluxo-wrap { position: relative; }
.fx-hint { font-size: 12px; color: #6c757d; margin: 10px 2px 14px; }
.fx-erro {
  font-size: 12.5px; color: #7b241c; background: #fdecea; border: 1px solid #f5c6cb;
  border-radius: 4px; padding: 8px 12px; margin: 0 2px 14px;
}
.fx-erro a { color: #7b241c; font-weight: 700; text-decoration: underline; margin-left: 4px; }
/* etiqueta que não vale em todos os tipos que compartilham a etapa */
.fx-flag-div { opacity: .75; border: 1px dashed rgba(0,0,0,.35); }
.text-purple { color: #7A3FC0; }
.text-cond { color: #B9791A; }

/* chips de tipo */
.fx-chips { display: flex; flex-wrap: wrap; gap: 7px; }
.fx-chip {
  display: inline-flex; align-items: center; gap: 7px; padding: 6px 11px; border-radius: 20px;
  border: 1px solid #ced4da; background: #fff; cursor: pointer; font-size: 13px; color: #495057;
  transition: all .12s;
}
.fx-chip:hover { border-color: var(--c); color: #23282c; }
.fx-chip.pinned { background: var(--c); border-color: var(--c); color: #fff; }
.fx-chip-dot { width: 9px; height: 9px; border-radius: 50%; background: var(--c); }
.fx-chip.pinned .fx-chip-dot { background: #fff; }
.fx-chip-count { font-size: 11px; font-weight: 700; background: rgba(0,0,0,.06); border-radius: 20px; padding: 0 7px; }
.fx-chip.pinned .fx-chip-count { background: rgba(255,255,255,.2); }

/* canvas */
.fx-card { overflow: hidden; }
.fx-scroll { overflow: auto; max-height: 62vh; padding: 30px 24px; }
.fx-diagram { position: relative; display: inline-block; min-width: 100%; }
.fx-cols { position: relative; z-index: 1; display: flex; align-items: stretch; gap: 62px; }
.fx-col { display: flex; flex-direction: column; justify-content: center; gap: 22px; }

.fx-svg { position: absolute; top: 0; left: 0; z-index: 0; overflow: visible; pointer-events: none; }
.fx-ln { fill: none; stroke: #b8c0cc; stroke-width: 2; transition: stroke .15s, opacity .15s; }
.fx-ah { fill: #b8c0cc; transition: fill .15s, opacity .15s; }
.fx-ln.fx-dim, .fx-ah.fx-dim { opacity: .1; }
/* transição condicional (ticket_etapa_condicional) */
.fx-ln.fx-cond { stroke: #B9791A; stroke-dasharray: 6 4; }
.fx-ah.fx-cond { fill: #B9791A; }

/* nós */
.fx-node { position: relative; z-index: 1; width: 188px; background: #fff; border: 1px solid #e2e6ec; border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0,0,0,.05); display: flex; overflow: hidden; transition: opacity .15s, border-color .15s, box-shadow .15s; }
.fx-root-node { width: 150px; }
.fx-stripe { width: 5px; background: #c6ccd6; flex: none; }
.fx-start .fx-stripe { background: #3a3e81; }
.fx-final .fx-stripe { background: #4CAF50; }
.fx-wait .fx-stripe { background: #FFC107; }
.fx-node-body { padding: 9px 11px; flex: 1; min-width: 0; }
.fx-node-head { display: flex; align-items: center; gap: 7px; margin-bottom: 3px; }
.fx-ord { flex: none; min-width: 20px; height: 20px; padding: 0 5px; border-radius: 6px; background: #eef0f4;
  color: #6c757d; border: 1px solid #e2e6ec; font-size: 11px; font-weight: 700; display: grid; place-items: center; }
.fx-node-title { font-size: 12.5px; font-weight: 600; line-height: 1.2; flex: 1; color: #23282c; }
.fx-zap { flex: none; width: 19px; height: 19px; border-radius: 6px; background: rgba(122,63,192,.12); color: #7A3FC0; display: grid; place-items: center; font-size: 11px; }
.fx-fork { flex: none; width: 19px; height: 19px; border-radius: 6px; background: rgba(185,121,26,.14); color: #B9791A; display: grid; place-items: center; font-size: 11px; cursor: help; }
.fx-node-key { font-family: monospace; font-size: 10px; color: #8a93a6; display: block; margin-bottom: 6px; word-break: break-all; }
.fx-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.fx-tags .badge { font-size: 10px; }

.fx-root { position: relative; width: 100%; padding: 12px 10px; border-radius: 10px; background: #3a3e81; color: #fff;
  display: flex; flex-direction: column; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 600; text-align: center; }
.fx-zap-corner { position: absolute; top: -8px; right: -8px; }

.fx-node.fx-hl { border-color: var(--hl); box-shadow: 0 0 0 2px var(--hl); }
.fx-node.fx-hl .fx-stripe { background: var(--hl); }
.fx-node.fx-hl .fx-ord { background: var(--hl); color: #fff; border-color: var(--hl); }
.fx-dim { opacity: .16; cursor: not-allowed; }

.fx-empty-all { text-align: center; padding: 60px 20px; }

/* popover — fixo p/ nao ser cortado pelo overflow do container */
.fx-pop { position: fixed; z-index: 1050; width: 240px; background: #fff; border: 1px solid #c6ccd6;
  border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,.15); padding: 12px; font-size: 12px;
  overflow-y: auto; overflow-x: hidden; }
.fx-pop-title { font-weight: 700; font-size: 13px; }
.fx-pop-key { font-family: monospace; font-size: 10px; color: #7A3FC0; display: block; margin-bottom: 8px; word-break: break-all; }
.fx-pop-row { display: flex; justify-content: space-between; gap: 10px; padding: 3px 0; border-top: 1px solid #eef0f4; }
.fx-pop-row .l { color: #8a93a6; } .fx-pop-row .v { font-weight: 600; text-align: right; }
.fx-pop-sub { font-size: 9.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: #8a93a6; margin: 9px 0 5px; }
.fx-pop-ord { display: flex; align-items: center; gap: 7px; padding: 2px 0; }
.fx-pop-ord .cd { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.fx-pop-ord .nm { flex: 1; color: #5b6478; } .fx-pop-ord .on { font-weight: 700; }
.fx-pop-muted { font-size: 11px; color: #8a93a6; padding: 2px 0; }
.fx-pop-erro { font-size: 11px; color: #C0392B; padding: 2px 0; }
.fx-pop-div { display: block; font-size: 10px; font-style: normal; color: #B9791A; }
.fx-pop-cond { display: flex; align-items: center; gap: 7px; padding: 2px 0; font-size: 11.5px; }
.fx-pop-cond .cd { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.fx-pop-cond .nm { flex: 1; color: #B9791A; font-weight: 600; }
.fx-pop-cond.in .nm { color: #8a6329; font-weight: 500; }
.fx-pop-cond .tp { color: #8a93a6; }
.fx-pop-auto { margin-top: 9px; padding: 8px 9px; border-radius: 8px; background: rgba(122,63,192,.1); font-size: 11px; display: flex; gap: 7px; align-items: flex-start; }
.fx-pop-auto .ai { color: #7A3FC0; margin-top: 1px; }

/* rail */
.fx-gatilho { border: 1px solid #e2e6ec; border-left: 3px solid #7A3FC0; border-radius: 8px; padding: 8px 10px; margin-bottom: 8px; }
.fx-gatilho:last-child { margin-bottom: 0; }
.fx-gatilho-ev { font-family: monospace; font-size: 10.5px; color: #7A3FC0; display: block; margin-top: 3px; }
.fx-gatilho-onde { font-size: 11px; color: #5b6478; margin-top: 4px; }
.fx-gatilho-act { display: flex; align-items: center; gap: 7px; margin-top: 6px; font-size: 12px; font-weight: 600; }
.fx-gatilho-click { cursor: pointer; transition: border-color .12s, box-shadow .12s, background .12s; }
.fx-gatilho-click:hover, .fx-gatilho-click:focus { border-color: #7A3FC0; background: rgba(122,63,192,.04); box-shadow: 0 1px 4px rgba(0,0,0,.08); outline: none; }
.fx-gatilho-more { margin-left: auto; font-size: 10.5px; font-weight: 600; color: #8a93a6; white-space: nowrap; }
.fx-campo { border: 1px solid #e2e6ec; border-left: 3px solid #2298a3; border-radius: 8px; padding: 8px 10px; margin-bottom: 8px; }
.fx-campo:last-child { margin-bottom: 0; }

/* rail — etapas condicionais */
.fx-cond-item { border: 1px solid #e2e6ec; border-left: 3px solid #B9791A; border-radius: 8px; padding: 8px 10px; margin-bottom: 8px; }
.fx-cond-item:last-child { margin-bottom: 0; }
.fx-cond-flow { display: flex; align-items: center; gap: 6px; font-size: 12px; }
.fx-cond-flow .fx-cond-de { color: #5b6478; }
.fx-cond-flow .fx-cond-para { font-weight: 700; color: #23282c; }
.fx-cond-arr { color: #B9791A; font-size: 10px; }
.fx-cond-item .cd { display: inline-block; width: 8px; height: 8px; border-radius: 50%; }
.fx-cond-orfa { margin-top: 10px; padding: 8px 10px; border-radius: 8px; background: rgba(192,57,43,.08); color: #C0392B; font-size: 11px; }
.fx-cond-orfa code { color: #C0392B; }
.fx-act-ic { width: 24px; height: 24px; border-radius: 6px; display: grid; place-items: center; flex: none; }
.fx-legend > div { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #5b6478; margin-bottom: 8px; }
.fx-legend .ldot { width: 13px; height: 13px; border-radius: 4px; flex: none; }
.fx-legend .lln { flex: none; }
.fx-legend .lln path { stroke: #B9791A; stroke-width: 2; stroke-dasharray: 6 4; fill: none; }

/* modal de detalhe da automação */
.fx-modal { padding: 4px 6px 0; }
.fx-modal-head { display: flex; gap: 12px; align-items: flex-start; padding-bottom: 12px; border-bottom: 1px solid #eef0f4; }
.fx-modal-head h4 { margin: 0 0 6px; font-size: 17px; }
.fx-modal-head-txt { min-width: 0; }
.fx-modal-badges { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.fx-modal-badges code { font-size: 10.5px; color: #8a93a6; }
.fx-act-ic-lg { width: 38px; height: 38px; border-radius: 9px; font-size: 16px; }
.fx-modal-sec { padding: 14px 0; border-bottom: 1px solid #eef0f4; }
.fx-modal-sec:last-child { border-bottom: 0; }
.fx-modal-sub { font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: #8a93a6; margin-bottom: 8px; }
.fx-modal-frase { font-size: 13px; color: #23282c; margin-bottom: 8px; }
.fx-modal-onde { display: flex; gap: 10px; font-size: 12px; }
.fx-modal-onde .l { color: #8a93a6; } .fx-modal-onde .v { font-weight: 600; color: #5b6478; }
.fx-modal-tab { font-size: 12px; margin-bottom: 0; }
.fx-modal-tab th { font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #8a93a6; }
.fx-modal-tab td { vertical-align: middle; word-break: break-word; }
.fx-modal-op { font-size: 10px; font-weight: 700; color: #8a93a6; }
.fx-modal-sql { background: #f6f7f9; border: 1px solid #e2e6ec; border-radius: 6px; padding: 7px 9px; font-size: 11px;
  margin: 0; white-space: pre-wrap; word-break: break-word; max-height: 190px; overflow: auto; }
.fx-modal-acao { border: 1px solid #e2e6ec; border-radius: 8px; padding: 9px 11px; margin-bottom: 8px; }
.fx-modal-acao:last-child { margin-bottom: 0; }
.fx-modal-acao-head { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; font-size: 13px; }
.fx-modal-acao-n { width: 20px; height: 20px; border-radius: 6px; background: #eef0f4; color: #6c757d;
  font-size: 11px; font-weight: 700; display: grid; place-items: center; flex: none; }
.fx-modal-classe { font-family: monospace; font-size: 11px; color: #7A3FC0; word-break: break-all; }
.fx-modal-params { margin-top: 8px; display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 4px 14px; }
.fx-modal-param { display: flex; justify-content: space-between; gap: 10px; font-size: 11.5px; border-top: 1px solid #eef0f4; padding: 3px 0; }
.fx-modal-param .l { color: #8a93a6; } .fx-modal-param .v { font-weight: 600; text-align: right; word-break: break-word; }

:fullscreen .fx-scroll { max-height: none; height: 100%; }
</style>
